import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { gtmLeadSubmit } from '../../lib/tracker';
import {
  ArrowRight, Sparkles, MessageCircle, Phone, X, CheckCircle2, Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 13 pest cards. Each card maps a user-facing pest to:
 *   - pestKey: value used to pre-select inside the global RealTimeCalculator
 *   - learnMore: optional pricing-page route (when the service has a dedicated page)
 *   - tone: Tailwind color tokens for the icon ring + glow accent
 *
 * When `learnMore` is falsy, the "Learn More" button falls back to opening the
 * calculator with the pest preselected — so the user still sees ONLY that
 * service's calculator, matching the spec.
 */
const PEST_SERVICES = [
  {
    id: 'cockroach', pestKey: 'cockroach', name: 'Cockroach Control', emoji: '🪳',
    desc: 'Gel bait + spray eliminates entire colonies. Safe for kitchens & families.',
    learnMore: '/pricing/cockroach-control',
    tone: { ring: 'ring-amber-200', bg: 'from-amber-50 to-amber-100', text: 'text-amber-700', glow: 'hover:shadow-amber-200/60' },
  },
  {
    id: 'termite', pestKey: 'termite', name: 'Termite Treatment', emoji: '🛡️',
    desc: 'Long-lasting chemical barrier protects furniture and structural wood.',
    learnMore: '/pricing/anti-termite-treatment',
    tone: { ring: 'ring-orange-200', bg: 'from-orange-50 to-orange-100', text: 'text-orange-700', glow: 'hover:shadow-orange-200/60' },
  },
  {
    id: 'bedbug', pestKey: 'bedbug', name: 'Bed Bug Removal', emoji: '🛏️',
    desc: 'Heat + chemical combo removes bed bugs from mattresses, seams and furniture.',
    learnMore: '/pricing/bed-bug-removal',
    tone: { ring: 'ring-rose-200', bg: 'from-rose-50 to-rose-100', text: 'text-rose-700', glow: 'hover:shadow-rose-200/60' },
  },
  {
    id: 'mosquito', pestKey: 'mosquito', name: 'Mosquito Control', emoji: '⚡',
    desc: 'Fogging + larvicidal treatment protects your home from dengue and malaria.',
    learnMore: '/pricing/mosquito-fogging',
    tone: { ring: 'ring-sky-200', bg: 'from-sky-50 to-sky-100', text: 'text-sky-700', glow: 'hover:shadow-sky-200/60' },
  },
  {
    id: 'rodent', pestKey: 'rodent', name: 'Rodent Control', emoji: '🐀',
    desc: 'Humane trapping + entry-point sealing. Safe for homes, godowns and offices.',
    learnMore: '/pricing/rodent-control',
    tone: { ring: 'ring-slate-200', bg: 'from-slate-50 to-slate-100', text: 'text-slate-700', glow: 'hover:shadow-slate-300/60' },
  },
  {
    id: 'ant', pestKey: 'ant', name: 'Ant Control', emoji: '🐜',
    desc: 'Colony-level gel baiting destroys the queen, not just visible workers.',
    learnMore: '/pricing/ant-control',
    tone: { ring: 'ring-red-200', bg: 'from-red-50 to-red-100', text: 'text-red-700', glow: 'hover:shadow-red-200/60' },
  },
  {
    id: 'spider', pestKey: 'spider', name: 'Spider Control', emoji: '🕷️',
    desc: 'Residual spray + web removal keeps corners, balconies and attics spider-free.',
    learnMore: '/pricing/spider-control',
    tone: { ring: 'ring-indigo-200', bg: 'from-indigo-50 to-indigo-100', text: 'text-indigo-700', glow: 'hover:shadow-indigo-200/60' },
  },
  {
    id: 'lizard', pestKey: 'lizard', name: 'Lizard Control', emoji: '🦎',
    desc: 'Odourless repellent treatment keeps lizards out of rooms, kitchens and balconies.',
    learnMore: '/pricing/lizard-control',
    tone: { ring: 'ring-emerald-200', bg: 'from-emerald-50 to-emerald-100', text: 'text-emerald-700', glow: 'hover:shadow-emerald-200/60' },
  },
  {
    id: 'fly', pestKey: 'fly', name: 'Fly Control', emoji: '🪰',
    desc: 'Targeted fly eradication for homes, restaurants and food-prep areas.',
    learnMore: '/pricing/fly-control',
    tone: { ring: 'ring-lime-200', bg: 'from-lime-50 to-lime-100', text: 'text-lime-700', glow: 'hover:shadow-lime-200/60' },
  },
  {
    id: 'silverfish', pestKey: 'silverfish', name: 'Silverfish Control', emoji: '🐛',
    desc: 'Protect books, clothes and paper archives from silverfish damage.',
    learnMore: '/pricing/silverfish-control',
    tone: { ring: 'ring-teal-200', bg: 'from-teal-50 to-teal-100', text: 'text-teal-700', glow: 'hover:shadow-teal-200/60' },
  },
  {
    id: 'waspbee', pestKey: 'other', name: 'Wasp / Bee Control', emoji: '🐝',
    desc: 'Safe hive relocation by certified handlers — we never kill pollinators.',
    learnMore: '/pricing/honeybee-removal',
    tone: { ring: 'ring-yellow-200', bg: 'from-yellow-50 to-yellow-100', text: 'text-yellow-700', glow: 'hover:shadow-yellow-200/60' },
  },
  {
    id: 'general', pestKey: 'general', name: 'General Pest Control', emoji: '🏠',
    desc: 'One integrated plan covering the most common household pests — best value.',
    learnMore: '/services',
    tone: { ring: 'ring-green-200', bg: 'from-green-50 to-green-100', text: 'text-green-700', glow: 'hover:shadow-green-200/60' },
  },
  // 13th = Other / Custom
  {
    id: 'other', pestKey: 'other', name: 'Custom Pest Control Solution', emoji: '✨',
    desc: 'Have something unusual? Describe your issue and we\'ll send an expert.',
    isOther: true,
    tone: { ring: 'ring-violet-200', bg: 'from-violet-50 to-violet-100', text: 'text-violet-700', glow: 'hover:shadow-violet-200/60' },
  },
];

export default function ServicesGrid({ onBook }) {
  const navigate = useNavigate();
  const [customOpen, setCustomOpen] = useState(false);

  const handleLearnMore = (svc) => {
    if (svc.isOther) { setCustomOpen(true); return; }
    if (svc.learnMore) navigate(svc.learnMore);
    else onBook?.(svc.pestKey); // no dedicated page → open calc with pest preselected
  };

  const handleBookNow = (svc) => {
    if (svc.isOther) { setCustomOpen(true); return; }
    onBook?.(svc.pestKey);
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-14 sm:py-20 lg:py-24 bg-gradient-to-b from-white via-slate-50 to-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-100 px-3 py-1 rounded-full">
            <Sparkles size={12} /> Our Services
          </span>
          <h2
            className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 leading-tight"
            data-testid="services-title"
            style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
          >
            Comprehensive Pest Control Solutions for Every Problem
          </h2>
          <p className="mt-3 text-sm sm:text-base text-gray-600 leading-relaxed">
            Pick the right treatment for your home or business — backed by certified experts, eco-friendly chemicals and guaranteed re-treatment warranty.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          {PEST_SERVICES.map((svc, i) => (
            <ServiceCard
              key={svc.id}
              svc={svc}
              index={i}
              onLearnMore={() => handleLearnMore(svc)}
              onBookNow={() => handleBookNow(svc)}
            />
          ))}
        </div>

        {/* After-grid CTA */}
        <div
          data-testid="services-bottom-cta"
          className="mt-10 sm:mt-14 rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-700 text-white p-6 sm:p-10 relative overflow-hidden"
        >
          <div aria-hidden className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight">
                Not sure which service you need?
              </h3>
              <p className="mt-2 text-sm sm:text-base text-white/85 leading-relaxed">
                Send us a WhatsApp message or book a free on-site inspection — we'll identify the issue and quote you transparently.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 md:flex-shrink-0">
              <Button
                onClick={() => onBook?.()}
                data-testid="services-free-inspection"
                className="h-12 min-h-[48px] px-6 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-bold shadow-lg"
              >
                Get Free Inspection <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <a
                href="https://api.whatsapp.com/send?phone=919410110910"
                target="_blank"
                rel="noreferrer"
                data-testid="services-whatsapp"
                className="h-12 min-h-[48px] px-6 rounded-full bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 inline-flex items-center justify-center gap-2 font-bold transition"
              >
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Custom inquiry modal */}
      {customOpen && (
        <CustomInquiryModal onClose={() => setCustomOpen(false)} />
      )}
    </section>
  );
}

/* ------------------------- CARD ------------------------- */
function ServiceCard({ svc, index, onLearnMore, onBookNow }) {
  return (
    <article
      data-testid={`service-card-${svc.id}`}
      className="group fade-in-up relative bg-white rounded-2xl p-5 sm:p-6 ring-1 ring-gray-100 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      {/* Breathing glow layer */}
      <span
        aria-hidden
        className={`breathe absolute inset-0 rounded-2xl pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 ${svc.tone.glow}`}
      />

      {/* Icon */}
      <div
        className={`relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br ${svc.tone.bg} ring-1 ${svc.tone.ring} flex items-center justify-center mb-4 shadow-inner`}
      >
        <span className="text-3xl sm:text-[32px] leading-none select-none" role="img" aria-label={svc.name}>
          {svc.emoji}
        </span>
      </div>

      {/* Name + desc */}
      <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">{svc.name}</h3>
      <p className="mt-1.5 text-xs sm:text-[13px] text-gray-600 leading-relaxed min-h-[3rem] sm:min-h-[3.5rem]">
        {svc.desc}
      </p>

      {/* Buttons */}
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={onLearnMore}
          data-testid={`service-learn-${svc.id}`}
          className={`flex-1 h-11 min-h-[44px] rounded-xl border border-slate-200 bg-white text-slate-800 text-xs sm:text-sm font-semibold hover:bg-slate-50 active:scale-[0.98] transition inline-flex items-center justify-center gap-1`}
        >
          Learn More
        </button>
        <button
          type="button"
          onClick={onBookNow}
          data-testid={`service-book-${svc.id}`}
          className="flex-1 h-11 min-h-[44px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md active:scale-[0.98] transition inline-flex items-center justify-center gap-1"
        >
          {svc.isOther ? 'Request Inspection' : 'Book Now'}
          <ArrowRight size={14} />
        </button>
      </div>
    </article>
  );
}

/* ------------------------- CUSTOM INQUIRY MODAL ------------------------- */
function CustomInquiryModal({ onClose }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [description, setDescription] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | success | error

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isValid = name.trim() && phone.length >= 10 && city.trim() && description.trim().length >= 5;

  const handleSubmit = async () => {
    if (!isValid || state === 'sending') return;
    setState('sending');
    try {
      const res = await axios.post(`${API_BASE}/leads`, {
        name,
        phone,
        city,
        pest_type: 'other',
        pest_label: 'Custom Pest Control Solution',
        pest_description: description,
        additional_details: description,
        estimated_price: null,
        source: 'services-grid:custom-inquiry',
      }, { timeout: 8000 });
      setState('success');
      gtmLeadSubmit({
        leadId: res?.data?.id || null,
        segment: 'residential',
        pestType: 'other',
        pestLabel: 'Custom Pest Control Solution',
        value: 0,
        city,
        source: 'services-grid:custom-inquiry',
      });
    } catch (err) {
      console.error('Custom inquiry failed', err);
      setState('error');
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200 px-3 sm:px-4"
      onClick={onClose}
      data-testid="custom-inquiry-modal"
    >
      <div
        className="bg-white w-full sm:max-w-lg max-h-[92vh] sm:max-h-[88vh] rounded-3xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-300 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white">
          <div>
            <h3 className="text-base sm:text-lg font-bold">Custom Pest Control Solution</h3>
            <p className="text-[11px] sm:text-xs text-violet-100 mt-0.5">Describe your issue — we'll dispatch an expert.</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="custom-inquiry-close"
            className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center active:scale-95 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        {state === 'success' ? (
          <div className="p-6 sm:p-8 text-center" data-testid="custom-inquiry-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
              <CheckCircle2 size={32} />
            </div>
            <h4 className="text-lg font-bold text-slate-900">Request received!</h4>
            <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
              Our expert will call you within the next 2 hours to schedule a free on-site inspection.
            </p>
            <Button
              onClick={onClose}
              className="mt-5 h-11 min-h-[44px] px-6 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="custom-inquiry-success-close"
            >
              Close
            </Button>
          </div>
        ) : (
          <div className="overflow-y-auto px-5 py-5 space-y-4">
            <div>
              <Label className="text-sm font-semibold text-slate-800">Describe your pest problem *</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Example: Saw unusual insects in my basement — black, 2cm long, many of them. Need urgent help."
                rows={3}
                className="mt-1.5 resize-none"
                data-testid="custom-inquiry-description"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-800">Name *</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="h-11 mt-1.5" data-testid="custom-inquiry-name" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-slate-800">Phone *</Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="10-digit phone"
                  className="h-11 mt-1.5"
                  maxLength={10}
                  data-testid="custom-inquiry-phone"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-800">City *</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g., Dehradun" className="h-11 mt-1.5" data-testid="custom-inquiry-city" />
            </div>

            {state === 'error' && (
              <p className="text-xs text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">
                Something went wrong. Please try WhatsApp instead: +91-9410110910
              </p>
            )}

            <div className="pt-1 space-y-2">
              <Button
                onClick={handleSubmit}
                disabled={!isValid || state === 'sending'}
                data-testid="custom-inquiry-submit"
                className={`w-full h-12 min-h-[48px] rounded-xl text-base font-semibold transition-all ${
                  isValid && state !== 'sending'
                    ? 'bg-violet-600 hover:bg-violet-700 text-white shadow-md'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {state === 'sending'
                  ? (<><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</>)
                  : 'Request Inspection'}
              </Button>
              <a
                href="tel:+917819988010"
                className="w-full h-11 min-h-[44px] rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold inline-flex items-center justify-center gap-2 transition"
                data-testid="custom-inquiry-call"
              >
                <Phone size={16} /> Or call us now
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
